import { Edge, Node } from 'reactflow';
import {
  ProductionWorkflowAdditionalTaskT,
  ProductionWorkflowResponsibleT,
  ProductionWorkflowTaskResponsibility,
} from 'services/production-workflow.model';
import { StateController } from 'state-controller';
import { ProductionWorkflowService } from 'services/production-workflow.service';
import { ProductionStatusEnum, TaskStatusEnum } from 'types/status-enums';
import { AppState } from 'redux/store';
import { ProductionTaskService } from 'services/production-task.service';
import { Actions as ProductionWorkflowActions } from 'pages/production-workflow/controllers/production-workflow.controller';
import {
  ManageQualityAdditionalTasks,
  ManageQualityControlBody,
  ReopenActionType,
  TaskReopenModel,
} from 'services/production-task.model';
import { ModalActions } from 'modules/root-modals/root-modals.controller';
import { DeleteConfirmationOwnProps } from 'modules/root-modals/modals/confirmation-modal/confirmation-modal';
import { MODALS } from 'modules/root-modals/modals';
import FireMinusIcon from 'icons/fire-minus';
import { IdName } from 'types/common-types';
import ConfirmationWindow from 'modules/confirmation-window/confirmation-window';
import {
  ProductionWorkflowTaskTWithIssues,
  Actions as ProductionAdditionalTasksActions,
} from 'pages/production-workflow/controllers/production-workflow-additional-tasks.controller';
import { Actions as TaskActions } from 'pages/task/task.controller';
import { Actions as ConfirmationWindowActions } from 'modules/confirmation-window/confirmation-window.controller';
import { PriorityEnum } from 'types/priority-enums';
import { ProductionFiltersActions } from 'pages/production/controllers/production-filters-controller/production-filters.controller';
import { getAdditionalTasksIssues } from 'pages/production-workflow/controllers/helpers';
import { notify } from 'notifications';
import { PaginationMode } from 'pages/production/controllers/production-filters-controller/constants';

export type PageT = 'production' | 'task' | 'production_list';

export enum FailedTabs {
  WorkflowTasks = 'workflow_tasks',
  AdditionalTasks = 'additional_tasks',
}

export type TaskData = {
  id: string;
  page: PageT;
  name: string;
  isFail: boolean;
  disable: boolean;
  isReopened: boolean;
  is_in_queue: boolean;
  isWithMoney: boolean;
  task_key: string;
  status: TaskStatusEnum;
  taskDepartmets: IdName[];
  is_reopened_with_payment: boolean;
  responsibilities: ProductionWorkflowTaskResponsibility[];
};

export type NestedWorkflowData = {
  id: string;
  page: PageT;
  name: string;
  version: number;
  workflow: string;
  progress: number;
  isForFail: boolean;
  configuration: string;
  status: ProductionStatusEnum;
  responsible: ProductionWorkflowResponsibleT;
};

export type TemporaryAdditionalTask = {
  name: string;
  id: string;
  basic_reward: number;
  is_control: boolean;
  is_in_queue: boolean;
  is_reopened: boolean;
  is_additional: boolean;
  time_limit: number;
  due_date: null;
  order: number;
  production_workflow_id: string;
  status: TaskStatusEnum;
  priority: PriorityEnum;
  total_in_progress_overtime: number;
  description: string;
  responsibilities: [
    {
      taskSlots: [
        {
          id: string;
          task_responsibility_id: string;
          taskAssignment: { user: { id: string } };
        },
      ];
    },
  ];
  issues: string[];
};

export type Nodes = Array<Node<TaskData, 'task'> | Node<NestedWorkflowData, 'workflow'>>;

export const MAX_REASONS_CHARS_COUNT = 250;

export type ManageFailedTasksState = {
  nodes: Nodes;
  edges: Edge[];
  page: PageT;
  isLoadingWorkflowTasks: boolean;
  isLoadingAdditionalTasks: boolean;
  productionId: string;
  productionStatus: string;
  isModalOpen: boolean;
  activeTab: FailedTabs;
  isAdditionalTasksLoaded: boolean;
  isReopeningTasks: boolean;
  newAdditionalTasks: TemporaryAdditionalTask[];
};

const defaultState: ManageFailedTasksState = {
  nodes: [],
  edges: [],
  page: 'production',
  productionStatus: '',
  isLoadingWorkflowTasks: false,
  isLoadingAdditionalTasks: false,
  productionId: '',
  isModalOpen: false,
  isReopeningTasks: false,
  newAdditionalTasks: [],
  activeTab: FailedTabs.WorkflowTasks,
  isAdditionalTasksLoaded: false,
};

const stateController = new StateController<ManageFailedTasksState>('MANAGE_FAILED_TASKS_STATE', defaultState);

export class Actions {
  public static openManageFailedTasksModal(productionId: string, page: PageT) {
    return (dispatch) => {
      dispatch(stateController.setState({ isModalOpen: true }));
      dispatch(Actions.initData(productionId, page));
    };
  }

  public static initAdditionalTasksData(productionId: string, spinner = true, resetLoadedState = true) {
    return async (dispatch, getState: () => AppState) => {
      const { isAdditionalTasksLoaded } = getState().manage_failed_tasks;

      if (isAdditionalTasksLoaded && resetLoadedState) return;

      try {
        if (spinner) dispatch(stateController.setState((prevState) => ({ ...prevState, isLoadingAdditionalTasks: true })));

        const additionalTasks = await ProductionWorkflowService.getAdditionalTasks(productionId);

        const additionalTaskWithIssues = additionalTasks
          .filter(
            (task) =>
              task.reopenedTasks?.length || !task.failed_task_id || (task.failed_task_id && task.status !== TaskStatusEnum.Done),
          )
          .map((task) => {
            const taskIssues = getAdditionalTasksIssues(task);

            const lastReopenedTask = task.reopenedTasks?.[task.reopenedTasks.length - 1] as
              | ProductionWorkflowAdditionalTaskT
              | undefined;

            const reopenedTask =
              lastReopenedTask?.id && lastReopenedTask.status === TaskStatusEnum.Done
                ? {
                    ...lastReopenedTask,
                    id: lastReopenedTask.id,
                    isFail: false,
                    disable: false,
                    isReopened: true,
                    isWithMoney: false,
                    name: lastReopenedTask.name,
                    status: lastReopenedTask.status,
                    task_key: lastReopenedTask.task_key,
                    is_in_queue: lastReopenedTask.is_in_queue,
                    taskDepartmets: lastReopenedTask.departmets_all,
                    taskResponsibilities: lastReopenedTask.taskResponsibilities,
                    failed_task_id: lastReopenedTask.failed_task_id,
                  }
                : undefined;

            return {
              ...task,
              issues: taskIssues,
              isFail: false,
              isReopened: false,
              isWithMoney: false,
              disable: !!task.reopenedTasks?.length,
              reopenedTask,
            };
          });

        dispatch(ProductionAdditionalTasksActions.onChange({ tasks: additionalTaskWithIssues }));

        dispatch(stateController.setState((prevState) => ({ ...prevState, isAdditionalTasksLoaded: true })));
      } catch (error) {
        notify.error(error.message);
      } finally {
        if (spinner) dispatch(stateController.setState((prevState) => ({ ...prevState, isLoadingAdditionalTasks: false })));
      }
    };
  }

  public static closeManageFailedTasksModal() {
    return (dispatch) => {
      dispatch(stateController.setState(defaultState));
      dispatch(ConfirmationWindowActions.disposeState());
    };
  }

  public static onChangeTab(tab: FailedTabs) {
    return (dispatch) => {
      dispatch(stateController.setState((prevState) => ({ ...prevState, activeTab: tab })));
    };
  }

  public static initData(id: string, page: PageT) {
    return async (dispatch) => {
      try {
        dispatch(
          stateController.setState((prevState) => ({ ...prevState, isReopeningTasks: true, isLoadingWorkflowTasks: true })),
        );

        const data = await ProductionWorkflowService.getProductionWorkflowInfo(id);
        const reasonOptions = await ProductionTaskService.getAllFailReasons();

        dispatch(ConfirmationWindowActions.setReasonOptions(reasonOptions));

        dispatch(
          stateController.setState((prev) => ({
            ...prev,
            page,
            productionId: id,
            productionStatus: data.status,
            edges: data.edges.map((edge) => ({
              id: edge.id,
              source: edge.source_id,
              target: edge.target_id,
              type: 'smart',
            })),
            nodes: data.productionWorkflowItems.flatMap((item): Nodes => {
              if (item.task) {
                const task: Node<TaskData, 'task'> = {
                  id: item.id,
                  type: 'task',
                  position: { x: item.position_x, y: item.position_y },
                  data: {
                    page,
                    isFail: false,
                    id: item.task.id,
                    isReopened: false,
                    isWithMoney: false,
                    name: item.task.name,
                    task_key: item.task.task_key,
                    status: item.task.status,
                    is_in_queue: item.task.is_in_queue,
                    taskDepartmets: item.task.departmets_all,
                    disable: !!item.task.reopened_tasks?.length,
                    responsibilities: item.task.responsibilities,
                    is_reopened_with_payment: item.task.is_reopened_with_payment,
                  },
                };

                const lastReopenedTask = item.task.reopened_tasks?.[item.task.reopened_tasks.length - 1];

                const reopenedTask: Node<TaskData, 'task'> | undefined = lastReopenedTask?.id
                  ? {
                      id: lastReopenedTask.id,
                      type: 'task',
                      position: { x: item.position_x + 37, y: item.position_y + 7 },
                      data: {
                        page,
                        isFail: false,
                        disable: false,
                        isReopened: true,
                        isWithMoney: false,
                        id: lastReopenedTask.id,
                        name: lastReopenedTask.name,
                        status: lastReopenedTask.status,
                        task_key: lastReopenedTask.task_key,
                        is_in_queue: lastReopenedTask.is_in_queue,
                        taskDepartmets: item.task.departmets_all,
                        responsibilities: lastReopenedTask.responsibilities,
                        is_reopened_with_payment: item.task.is_reopened_with_payment,
                      },
                    }
                  : undefined;

                return [task, reopenedTask].filter((reopenTask) => !!reopenTask) as Nodes;
              }

              if (item.nestedProductionWorkflow) {
                const nestedWorkflow: Node<NestedWorkflowData, 'workflow'> = {
                  id: item.id,
                  type: 'workflow',
                  position: { x: item.position_x, y: item.position_y },
                  data: {
                    page,
                    isForFail: true,
                    id: item.nestedProductionWorkflow.id,
                    name: item.nestedProductionWorkflow.title,
                    status: item.nestedProductionWorkflow.status,
                    version: item.nestedProductionWorkflow.version,
                    progress: item.nestedProductionWorkflow.progress,
                    workflow: item.nestedProductionWorkflow.workflow,
                    responsible: item.nestedProductionWorkflow.responsible,
                    configuration: item.nestedProductionWorkflow.configuration,
                  },
                };

                return [nestedWorkflow];
              }

              return [];
            }),
          })),
        );
      } finally {
        dispatch(
          stateController.setState((prevState) => ({ ...prevState, isReopeningTasks: false, isLoadingWorkflowTasks: false })),
        );
      }
    };
  }

  public static handleFailTask(taskId: string, values: Partial<TaskData>) {
    return (dispatch) => {
      dispatch(
        stateController.setState((prev) => ({
          ...prev,
          nodes: prev.nodes.map((node) => {
            return node.type === 'task' && node.data.id === taskId ? { ...node, data: { ...node.data, ...values } } : node;
          }),
        })),
      );
    };
  }

  public static openApplyChangesConfirmationModal() {
    return (dispatch, getState: () => AppState) => {
      const { nodes, newAdditionalTasks } = getState().manage_failed_tasks;
      const { status } = getState().production_workflow.productionInfo;

      const { tasks: additionalTasksToReopen } = getState().production_workflow_additional_tasks;

      const isProductionHasDoneStatus = status === ProductionStatusEnum.Done;

      const tasksToReopen: TaskReopenModel = [];
      const tasksToReopenWithPayment: TaskReopenModel = [];

      nodes.forEach((node) => {
        if (node.type !== 'task' || !node.data.isFail) return;

        const taskData = {
          id: node.data.id,
          taskKey: node.data.task_key,
          name: node.data.name,
        };

        if (!node.data.isWithMoney) {
          tasksToReopen.push(taskData);
        }

        if (node.data.isWithMoney) {
          tasksToReopenWithPayment.push(taskData);
        }
      });

      additionalTasksToReopen.forEach((t) => {
        const current = t.reopenedTask ?? t;

        if (!current.isFail) return;

        const taskData = {
          id: t.id,
          taskKey: t.task_key,
          name: t.name,
        };

        if (!current.isWithMoney) {
          tasksToReopen.push(taskData);
        }

        if (current.isWithMoney) {
          tasksToReopenWithPayment.push(taskData);
        }
      });

      const countItemsForManageQualityControl =
        newAdditionalTasks.length + tasksToReopen.length + tasksToReopenWithPayment.length;

      dispatch(
        ModalActions.openModal<DeleteConfirmationOwnProps>({
          id: MODALS.CONFIRM,
          props: {
            title: <>Apply changes?</>,
            text: (
              <ConfirmationWindow
                description="Indicate the reason why task(s) didn’t pass the quality control. It will be saved and appear at production in task(s) that
                didn’t pass the quality control."
                warningText={
                  <p>
                    Adding task or failing tasks in the finished production will cause the production status to change to &quot;In
                    progress&quot;
                  </p>
                }
                tasksToReopen={tasksToReopen}
                additionalTasksToAdd={newAdditionalTasks}
                tasksToReopenWithPayment={tasksToReopenWithPayment}
                isProductionHasDoneStatus={isProductionHasDoneStatus}
                onReasonChange={(value) => dispatch(ConfirmationWindowActions.onReasonChange(value))}
                onReasonParametersChange={(value) => dispatch(ConfirmationWindowActions.onReasonParametersChange(value.id))}
              />
            ),
            icon: <FireMinusIcon />,
            withCloseButton: false,
            actionText: 'Apply',
            countItems: countItemsForManageQualityControl,
            action: () => dispatch(Actions.handleApply()),
            cancelAction: () => dispatch(ConfirmationWindowActions.disposeState()),
            onClickAway: () => dispatch(ConfirmationWindowActions.disposeState()),
          },
        }),
      );
    };
  }

  public static updateAdditionalTasks(additionalTask: ProductionWorkflowTaskTWithIssues | undefined, temporaryId: string) {
    return (dispatch) => {
      dispatch(
        stateController.setState((prevState) => {
          if (!additionalTask) return prevState;

          const taskExists = prevState.newAdditionalTasks.some((task) => task.id === temporaryId);

          const updatedTasks = taskExists
            ? prevState.newAdditionalTasks.map((task) => (task.id === temporaryId ? additionalTask : task))
            : [...prevState.newAdditionalTasks, additionalTask];

          return {
            ...prevState,
            newAdditionalTasks: updatedTasks,
          };
        }),
      );
    };
  }

  public static handleApply() {
    return async (dispatch, getState: () => AppState) => {
      const { productionId, nodes, newAdditionalTasks, page } = getState().manage_failed_tasks;

      const { reasonForReopening, selectedReasonOptions } = getState().confirmation_window;

      const { tasks } = getState().production_workflow_additional_tasks;

      const taskId = getState().task.task.id;

      const additionalTasksToReopen = tasks
        .filter((t) => {
          const current = t.reopenedTask ?? t;
          return current.isFail;
        })
        .map((t) => {
          const current = t.reopenedTask ?? t;
          return {
            id: current.id,
            reopen_action_type:
              current.isFail && current.isWithMoney ? ReopenActionType.ReopenWithNewPayment : ReopenActionType.Reopen,
          };
        });

      const tasksForReopen = nodes
        .filter((node) => node.type === 'task' && node.data.isFail)
        .map((node) => ({
          id: node.data.id,
          reopen_action_type:
            node.type === 'task' && node.data.isFail && node.data.isWithMoney
              ? ReopenActionType.ReopenWithNewPayment
              : ReopenActionType.Reopen,
        }));

      const allTasksToReopen = [...additionalTasksToReopen, ...tasksForReopen];

      const reasonIds = selectedReasonOptions.map((reason) => reason.id);

      const newAdditionalTasksToAdd: ManageQualityAdditionalTasks[] = newAdditionalTasks.map((t) => ({
        production_workflow_id: t.production_workflow_id,
        name: t.name,
        description: t.description,
        is_additional: t.is_additional,
        is_in_queue: t.is_in_queue,
        time_limit: t.time_limit,
        order: t.order,
        basic_reward: t.basic_reward,
        due_date: t.due_date,
      }));

      const body: ManageQualityControlBody = {
        production_id: productionId,
        additional_tasks: newAdditionalTasksToAdd || [],
      };

      if (reasonIds && reasonIds.length > 0) {
        body.fail_reason_ids = reasonIds;
      }

      if (reasonForReopening?.trim()) {
        body.comment = reasonForReopening;
      }

      if (allTasksToReopen && allTasksToReopen.length > 0) {
        body.manage_failed_tasks = allTasksToReopen;
      }

      await ProductionTaskService.manageQualityControl(body);

      if (page === 'production') {
        await dispatch(ProductionWorkflowActions.silentLoad({ id: productionId, disableAdditionalTasksSet: false }));
      }

      if (page === 'task') {
        await dispatch(Actions.initAdditionalTasksData(productionId, false, false));
        await dispatch(TaskActions.init(taskId, false));
      }

      if (page === 'production_list') {
        await dispatch(Actions.initAdditionalTasksData(productionId, false, false));
        dispatch(
          ProductionFiltersActions.getProductionsByFilter({
            showFetchEffect: false,
            paginationMode: PaginationMode.CurrentPage,
            resetSelectedProductions: false,
          }),
        );
      }

      if (tasksForReopen.length) {
        dispatch(Actions.closeManageFailedTasksModal());
        return;
      }

      dispatch(ConfirmationWindowActions.disposeState());

      dispatch(
        stateController.setState((prevState) => ({
          ...prevState,
          newAdditionalTasks: [],
          isLoadingWorkflowTasks: false,
          isLoadingAdditionalTasks: false,
          isModalOpen: true,
        })),
      );
    };
  }
}

export class Selectors {}

export const reducer = stateController.getReducer();
